import { PermissionName, useAuthContext } from 'components/auth';
import { PageName } from 'lib/constants';
import _ from 'lodash';
import { useRouter } from 'next/router';
import { ReactNode } from 'react';
import { KeyedMutator } from 'swr';
import * as api from './api';
import { OnboardingContext } from './context';
import { isOnboardingStepComplete } from './helpers';
import { useOnboardingInfo } from './hooks';
import {
  IAdminOnboardingInfo,
  OnboardingStep,
  OnboardingTooltipType,
} from './types';

interface IOnboardingContextProviderProps {
  children: ReactNode;
  onboardingInfo?: IAdminOnboardingInfo;
}

export const OnboardingContextProvider = ({
  children,
  onboardingInfo: initialOnboardingInfo,
}: IOnboardingContextProviderProps) => {
  const { asPath } = useRouter();
  const { getIsInPermission } = useAuthContext();

  const { data, mutate } = useOnboardingInfo({
    fallbackData: initialOnboardingInfo,
    shouldFetch: getIsInPermission(PermissionName.COMPANY_ONBOARDING),
  });

  const isOnboardingComplete = data?.isOnboardingComplete ?? false;
  const onboardingSteps = data?.onboardingSteps ?? [];

  const areAllStepsComplete =
    !_.isEmpty(onboardingSteps) &&
    _.every(onboardingSteps, isOnboardingStepComplete);
  const nextOnboardingStep = _.find(
    onboardingSteps,
    (s) => !isOnboardingStepComplete(s)
  )?.type;

  const completeOnboarding = async () => {
    await api.completeOnboarding();
    await refreshOnboarding();
  };

  const completeStep = async (type: OnboardingStep) => {
    const onboardingStep = _.find(onboardingSteps, (s) => s.type === type);
    if (!isOnboardingStepComplete(onboardingStep)) {
      await api.completeStep(type);
      await refreshOnboarding();
    }
  };

  const getIsStepComplete = (type: OnboardingStep) => {
    const onboardingStep = _.find(onboardingSteps, (s) => s.type === type);
    return isOnboardingStepComplete(onboardingStep);
  };

  const getIsTooltipActive = (tooltipType: OnboardingTooltipType) => {
    switch (tooltipType) {
      case OnboardingTooltipType.HELP:
        return areAllStepsComplete;
      case OnboardingTooltipType.SCHEDULE_ASSIGNMENT:
        return (
          nextOnboardingStep === OnboardingStep.SCHEDULE_ASSIGNMENT &&
          !_.includes(asPath, PageName.ADMIN_DASHBOARD.toLowerCase())
        );
      default:
        return false;
    }
  };

  const refreshOnboarding: KeyedMutator<IAdminOnboardingInfo> = (...args) => {
    if (!isOnboardingComplete) {
      return mutate(...args);
    }
  };

  return (
    <OnboardingContext.Provider
      value={{
        areAllStepsComplete,
        completeOnboarding,
        completeStep,
        getIsStepComplete,
        getIsTooltipActive,
        isOnboardingComplete,
        nextOnboardingStep,
        onboardingSteps,
        refreshOnboarding,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
